import { useEffect, useMemo, useRef, useState, type SyntheticEvent } from "react";
import {
  buildMailBodyDocumentCached,
  type MailBodyDocumentKey,
} from "@/lib/message/mailBodyDocumentCache";
import { bindMailBodyExternalLinks } from "@/lib/mail/openExternalUrl";
import {
  allowSenderRemoteImages,
  applyRemoteImagePolicy,
  htmlHasRemoteImages,
  isSenderAllowedRemoteImages,
} from "@/lib/message/remoteImages";
import { RemoteImagesBanner } from "./RemoteImagesBanner";

type Props = {
  html: string;
  text: string;
  from?: string;
  messageKey?: MailBodyDocumentKey;
  /** pane = remplit le panneau lecture ; flow = hauteur naturelle (réponse inline). */
  layout?: "pane" | "flow";
};

/**
 * Sandbox : same-origin (mesure hauteur + interception liens) + popups en secours.
 * Les clics http(s) sont interceptés et ouverts hors PWA (navigateur système).
 */
const IFRAME_SANDBOX = "allow-same-origin allow-popups allow-popups-to-escape-sandbox";

const FLOW_BODY_MIN_HEIGHT = 280;

export function MessageBodyView({ html, text, from = "", messageKey, layout = "pane" }: Props) {
  const unlinkRef = useRef<(() => void) | null>(null);
  const flowHeightRef = useRef(FLOW_BODY_MIN_HEIGHT);
  const [allowThisMessage, setAllowThisMessage] = useState(false);
  const [senderAllowed, setSenderAllowed] = useState(() =>
    from ? isSenderAllowedRemoteImages(from) : false
  );

  useEffect(() => {
    setAllowThisMessage(false);
    setSenderAllowed(from ? isSenderAllowedRemoteImages(from) : false);
  }, [from, messageKey?.accountId, messageKey?.folder, messageKey?.uid]);

  const allowRemote = allowThisMessage || senderAllowed;
  const hasRemote = useMemo(() => htmlHasRemoteImages(html), [html]);
  const showBanner = Boolean(html.trim()) && hasRemote && !allowRemote;

  const srcDoc = useMemo(() => {
    if (!html.trim()) return "";
    const base = buildMailBodyDocumentCached(html, messageKey);
    return applyRemoteImagePolicy(base, allowRemote);
  }, [html, messageKey, allowRemote]);

  const iframeKey = messageKey
    ? `${messageKey.accountId}-${messageKey.folder}-${messageKey.uid}-${allowRemote ? "r" : "b"}`
    : `${allowRemote ? "r" : "b"}`;

  useEffect(() => () => {
    unlinkRef.current?.();
    unlinkRef.current = null;
  }, []);

  function attachExternalLinks(iframe: HTMLIFrameElement) {
    unlinkRef.current?.();
    unlinkRef.current = null;
    const doc = iframe.contentDocument;
    if (!doc) return;
    unlinkRef.current = bindMailBodyExternalLinks(doc);
  }

  function handleIframeLoad(event: SyntheticEvent<HTMLIFrameElement>) {
    const iframe = event.currentTarget;
    attachExternalLinks(iframe);
    if (layout !== "flow") return;
    const doc = iframe.contentDocument;
    if (!doc) return;
    const body = doc.body;
    const root = doc.documentElement;
    const measured = Math.max(
      body?.scrollHeight ?? 0,
      body?.offsetHeight ?? 0,
      root?.scrollHeight ?? 0,
      root?.offsetHeight ?? 0,
      FLOW_BODY_MIN_HEIGHT
    );
    const next = measured + 8;
    if (Math.abs(next - flowHeightRef.current) < 2) return;
    flowHeightRef.current = next;
    iframe.style.height = `${next}px`;
  }

  function handleShowOnce() {
    setAllowThisMessage(true);
  }

  function handleAlwaysForSender() {
    if (from) {
      allowSenderRemoteImages(from);
    }
    setSenderAllowed(true);
    setAllowThisMessage(true);
  }

  const banner = showBanner ? (
    <RemoteImagesBanner
      from={from}
      onShowOnce={handleShowOnce}
      onAlwaysForSender={handleAlwaysForSender}
    />
  ) : null;

  if (srcDoc) {
    if (layout === "flow") {
      return (
        <div className="mail-reader-body-flow relative w-full bg-[var(--mail-surface)]">
          {banner}
          <iframe
            key={iframeKey}
            title="Corps du message"
            sandbox={IFRAME_SANDBOX}
            srcDoc={srcDoc}
            onLoad={handleIframeLoad}
            style={{ height: flowHeightRef.current, colorScheme: "light", background: "#ffffff" }}
            className="mail-body-content-enter block w-full border-0 bg-white"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      );
    }

    return (
      <div className="absolute inset-0 flex flex-col">
        {banner}
        <div className="relative min-h-0 flex-1">
          <iframe
            key={iframeKey}
            title="Corps du message"
            sandbox={IFRAME_SANDBOX}
            srcDoc={srcDoc}
            onLoad={handleIframeLoad}
            style={{ colorScheme: "light", background: "#ffffff" }}
            className="mail-body-content-enter absolute inset-0 h-full w-full border-0 bg-white"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    );
  }

  if (text.trim()) {
    if (layout === "flow") {
      return (
        <div className="mail-reader-body-flow mail-body-content-enter w-full bg-white p-4">
          <pre className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-900">
            {text}
          </pre>
        </div>
      );
    }

    return (
      <div className="mail-body-content-enter absolute inset-0 overflow-y-auto bg-white p-4">
        <pre className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-900">
          {text}
        </pre>
      </div>
    );
  }

  if (layout === "flow") {
    return (
      <div className="mail-reader-body-flow flex min-h-[12rem] w-full items-center justify-center bg-[var(--mail-surface)] p-4">
        <p className="text-sm text-[var(--mail-muted)]">Ce message n’a pas de contenu — seulement l’objet.</p>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-[var(--mail-surface)] p-4">
      <p className="text-sm text-[var(--mail-muted)]">Ce message n’a pas de contenu — seulement l’objet.</p>
    </div>
  );
}
